/**
 * Developer landing page — code-first pitch for the m8tes platform.
 *
 * Sections: Hero → Quick Start → Infrastructure → Build for Users → Automate Ops → SDK Showcase → Use Cases → CTA.
 * Minimalistic: more sections, less text per section. Every section scannable in 3 seconds.
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import {
	ArrowUpRight,
	BarChart3,
	Blocks,
	Box,
	Brain,
	Clock,
	FileOutput,
	KeyRound,
	Mail,
	MessageSquare,
	Radio,
	RefreshCw,
	Shield,
	ShieldCheck,
	Users,
	Webhook,
} from "lucide-react";
import { PublicLayout } from "@/components/public-layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
	buildBreadcrumb,
	buildSoftwareApplication,
	buildWebPage,
	jsonLdScript,
} from "@/lib/seo/json-ld";
import { buildLinks, buildMetaTags, getCanonical } from "@/lib/seo/meta";

export const Route = createFileRoute("/developers")({
	head: () => {
		const canonical = getCanonical("/developers");
		const meta = buildMetaTags({
			title: "Developer API — Build Autonomous AI Teammates",
			description:
				"Hosted runtime with sandboxed execution and 70+ integrations. Streaming, scheduling, webhooks, memory, permissions, managed OAuth. Python SDK + REST API.",
			ogType: "website",
		});

		const schemas = [
			buildWebPage({
				name: "Developer API — Build Autonomous AI Teammates",
				description:
					"Hosted runtime with sandboxed execution and 70+ integrations. Streaming, scheduling, webhooks, memory, permissions, managed OAuth.",
				url: canonical,
			}),
			buildBreadcrumb([
				{ name: "Home", url: getCanonical("/") },
				{ name: "Developers", url: canonical },
			]),
			buildSoftwareApplication({ price: "0" }),
		];

		return {
			meta,
			links: buildLinks({ canonical }),
			scripts: schemas.map(jsonLdScript),
		};
	},

	component: DevelopersPage,
});

function DevelopersPage() {
	return (
		<PublicLayout className="mx-auto w-full max-w-6xl gap-24 px-4 py-16 text-foreground sm:px-6 lg:px-8">
			<HeroSection />
			<QuickStartSection />
			<InfrastructureSection />
			<CodeShowcaseSection />
			<ProductSection />
			<OpsSection />
			<UseCaseSection />
			<FinalCtaSection />
		</PublicLayout>
	);
}

/* ── Hero ─────────────────────────────────────────────────────────────── */

function HeroSection() {
	return (
		<section className="relative isolate overflow-hidden rounded-3xl border bg-card/90 px-6 py-16 text-center shadow-lg">
			<div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-br from-primary/15 via-transparent to-secondary/20" />
			<Badge variant="outline" className="mx-auto w-fit text-xs uppercase tracking-wide">
				Developer Platform
			</Badge>
			<h1 className="mt-6 text-balance text-4xl font-semibold leading-tight tracking-tight text-foreground sm:text-5xl">
				Build autonomous teammates into your product
			</h1>
			<p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
				Hosted runtime with sandboxed execution, managed OAuth, and 70+ integrations.
			</p>
			<div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
				<Button size="lg" asChild>
					<Link to="/signup">Start building free</Link>
				</Button>
				<Button variant="ghost" size="lg" asChild>
					<Link to="/docs" className="inline-flex items-center gap-1">
						Read the docs <ArrowUpRight className="size-4" />
					</Link>
				</Button>
			</div>
			<p className="mt-4 text-sm text-muted-foreground">
				Get started for free · No credit card ·{" "}
				<Link to="/pricing" className="underline underline-offset-4 hover:text-foreground">
					See pricing
				</Link>
			</p>
		</section>
	);
}

/* ── Quick Start ──────────────────────────────────────────────────────── */

function QuickStartSection() {
	return (
		<section className="space-y-6">
			<div className="text-center">
				<h2 className="text-3xl font-semibold">From zero to running teammate in seconds</h2>
				<p className="mt-2 text-muted-foreground">Install the SDK, create a run, stream results.</p>
			</div>

			<div className="overflow-hidden rounded-2xl border bg-slate-950 text-slate-50 shadow-lg">
				<div className="border-b border-slate-800 px-5 py-3">
					<code className="text-xs text-slate-400">pip install m8tes</code>
				</div>
				<Tabs defaultValue="python" className="w-full">
					<TabsList className="justify-start rounded-none border-b border-slate-800 bg-transparent px-5">
						<TabsTrigger value="python" className={TAB_TRIGGER_CLASS}>
							Python
						</TabsTrigger>
						<TabsTrigger value="curl" className={TAB_TRIGGER_CLASS}>
							curl
						</TabsTrigger>
						<TabsTrigger value="cli" className={TAB_TRIGGER_CLASS}>
							CLI
						</TabsTrigger>
					</TabsList>
					<TabsContent value="python" className="mt-0 p-5">
						<pre className="overflow-x-auto font-mono text-sm leading-relaxed">
							{`from m8tes import M8tes

client = M8tes()

for event in client.runs.create(message="Summarize the latest AI news"):
    print(event.type, event.raw)`}
						</pre>
					</TabsContent>
					<TabsContent value="curl" className="mt-0 p-5">
						<pre className="overflow-x-auto font-mono text-sm leading-relaxed">
							{`curl -N -X POST https://m8tes.ai/api/v2/runs \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"message": "Summarize the latest AI news"}'`}
						</pre>
					</TabsContent>
					<TabsContent value="cli" className="mt-0 p-5">
						<pre className="overflow-x-auto font-mono text-sm leading-relaxed">
							{`# Login
m8tes auth login

# Run a task
m8tes mate task "Summarize the latest AI news"`}
						</pre>
					</TabsContent>
				</Tabs>
			</div>

			<div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
				{STATS.map((stat) => (
					<div key={stat.label} className="rounded-2xl border bg-card/80 px-4 py-3 text-center">
						<p className="text-lg font-semibold text-foreground">{stat.value}</p>
						<p className="text-xs text-muted-foreground">{stat.label}</p>
					</div>
				))}
			</div>
		</section>
	);
}

/* ── Infrastructure (contained 3-col grid) ───────────────────────────── */

function InfrastructureSection() {
	return (
		<section className="rounded-3xl border bg-muted/30 p-6 sm:p-8">
			<div className="text-center">
				<h2 className="text-3xl font-semibold">
					Stop building and maintaining agent infrastructure
				</h2>
				<p className="mt-2 text-muted-foreground">We handle the plumbing — you write the logic.</p>
			</div>
			<div className="mt-6 grid gap-4 sm:grid-cols-2 md:grid-cols-3">
				{INFRASTRUCTURE_ITEMS.map((item) => {
					const Icon = item.icon;
					return (
						<div
							key={item.title}
							className="hover-lift rounded-xl border bg-card/80 p-5 transition-transform"
						>
							<Icon className="size-5 text-muted-foreground" />
							<p className="mt-3 font-medium text-foreground">{item.title}</p>
							<p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
						</div>
					);
				})}
			</div>
		</section>
	);
}

/* ── Build for your users (asymmetric split: text left, grid right) ─── */

function ProductSection() {
	return (
		<section className="grid gap-8 lg:grid-cols-[1fr_1.5fr]">
			<div className="flex flex-col justify-center">
				<h2 className="text-3xl font-semibold leading-tight">Add AI teammates to your product</h2>
				<p className="mt-2 text-muted-foreground">
					Everything you need to ship AI features to your users.
				</p>
			</div>
			<div className="grid gap-4 sm:grid-cols-2">
				{PRODUCT_FEATURES.map((item) => {
					const Icon = item.icon;
					return (
						<div
							key={item.title}
							className="hover-lift surface-glass-interactive surface-divider rounded-xl border p-5 transition-transform"
						>
							<Icon className="size-5 text-muted-foreground" />
							<p className="mt-3 font-medium text-foreground">{item.title}</p>
							<p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
						</div>
					);
				})}
			</div>
		</section>
	);
}

/* ── Automate your ops (2-col wide cards) ────────────────────────────── */

function OpsSection() {
	return (
		<section className="space-y-6">
			<div className="text-center">
				<h2 className="text-3xl font-semibold">Automate ops across your organization</h2>
				<p className="mt-2 text-muted-foreground">
					Implement AI teammates with clear ROI. Replace manual workflows with autonomous execution.
				</p>
			</div>
			<div className="grid gap-4 sm:grid-cols-2">
				{OPS_FEATURES.map((item) => {
					const Icon = item.icon;
					return (
						<div
							key={item.title}
							className="hover-lift flex items-start gap-4 rounded-xl border p-5 transition-transform"
						>
							<Icon className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
							<div>
								<p className="font-medium text-foreground">{item.title}</p>
								<p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
							</div>
						</div>
					);
				})}
			</div>
		</section>
	);
}

/* ── SDK Showcase ────────────────────────────────────────────────────── */

function CodeShowcaseSection() {
	return (
		<section className="grid gap-8 lg:grid-cols-[1fr_1.2fr]">
			<div className="flex flex-col justify-center space-y-5">
				<h2 className="text-3xl font-semibold leading-tight">Simple SDK, powerful teammates</h2>
				<div className="space-y-3">
					<BulletItem title="Create">Define tools, instructions, and triggers.</BulletItem>
					<BulletItem title="Run">Stream results to your UI in real-time.</BulletItem>
					<BulletItem title="Automate">Schedule, trigger, repeat.</BulletItem>
				</div>
				<Link
					to="/docs"
					className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
				>
					Read the docs <ArrowUpRight className="size-3.5" />
				</Link>
			</div>
			<div className="overflow-hidden rounded-2xl border bg-slate-950 p-5 text-slate-50 shadow-lg">
				<pre className="overflow-x-auto font-mono text-sm leading-relaxed">
					{`from m8tes import M8tes

client = M8tes()

# Create a reusable teammate with tools
bot = client.teammates.create(
    name="Support Bot",
    tools=["gmail", "slack"],
    instructions="Handle customer tickets"
)

# Run with streaming
for event in client.runs.create(
    teammate_id=bot.id,
    message="Close resolved tickets"
):
    print(event.type, event.raw)

# Schedule recurring work
task = client.tasks.create(
    teammate_id=bot.id,
    instructions="Generate weekly report",
)
client.tasks.triggers.create(
    task.id, type="schedule", cron="0 9 * * 1"
)

# Per-user memory for multi-tenancy
client.memories.create(
    user_id="customer_123",
    content="Prefers email over Slack"
)`}
				</pre>
			</div>
		</section>
	);
}

/* ── Use Cases ────────────────────────────────────────────────────────── */

function UseCaseSection() {
	return (
		<section className="rounded-3xl border bg-muted/30 p-8">
			<h2 className="text-center text-3xl font-semibold">What developers are building</h2>
			<div className="mt-6 grid gap-4 md:grid-cols-3">
				{USE_CASES.map((uc) => (
					<div
						key={uc.title}
						className="hover-lift rounded-2xl border bg-card/80 p-6 transition-transform"
					>
						<p className="font-semibold text-foreground">{uc.title}</p>
						<p className="mt-1 text-sm text-muted-foreground">{uc.description}</p>
					</div>
				))}
			</div>
		</section>
	);
}

/* ── Final CTA ────────────────────────────────────────────────────────── */

function FinalCtaSection() {
	return (
		<section className="rounded-3xl border bg-gradient-to-br from-primary to-primary/90 px-6 py-12 text-center text-primary-foreground shadow-lg">
			<h2 className="text-3xl font-semibold">Ready to ship your first AI teammate?</h2>
			<p className="mx-auto mt-3 max-w-xl text-primary-foreground/90">
				Get started free. Ship in minutes.
			</p>
			<div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
				<Button size="lg" variant="secondary" asChild>
					<Link to="/signup">Start building</Link>
				</Button>
				<Button size="lg" variant="ghost" className="text-primary-foreground" asChild>
					<Link to="/docs" className="inline-flex items-center gap-1">
						Read the docs <ArrowUpRight className="size-4" />
					</Link>
				</Button>
			</div>
		</section>
	);
}

/* ── Shared Components ───────────────────────────────────────────────── */

function BulletItem({ title, children }: { title: string; children: React.ReactNode }) {
	return (
		<div>
			<p className="font-medium text-foreground">{title}</p>
			<p className="text-sm text-muted-foreground">{children}</p>
		</div>
	);
}

/* ── Data ─────────────────────────────────────────────────────────────── */

interface FeatureItem {
	title: string;
	description: string;
	icon: LucideIcon;
}

const TAB_TRIGGER_CLASS =
	"flex-none rounded-none border-b-2 border-transparent text-slate-400 data-[state=active]:border-slate-50 data-[state=active]:bg-transparent data-[state=active]:text-slate-50 data-[state=active]:shadow-none";

const STATS = [
	{ label: "Integrations", value: "70+" },
	{ label: "Real-time streaming", value: "SSE" },
	{ label: "No credit card required", value: "Free" },
	{ label: "To first running teammate", value: "< 2 min" },
];

const INFRASTRUCTURE_ITEMS: FeatureItem[] = [
	{
		title: "Sandboxed runtime",
		description: "Isolated execution environment. No servers to manage.",
		icon: Box,
	},
	{
		title: "Managed OAuth",
		description: "70+ providers. We handle token refresh and re-auth.",
		icon: KeyRound,
	},
	{
		title: "Streaming",
		description: "SSE with typed events. Push results to your UI in real-time.",
		icon: Radio,
	},
	{
		title: "Cost tracking",
		description: "Per-message token and cost tracking out of the box.",
		icon: BarChart3,
	},
	{
		title: "Error handling",
		description: "Retries, timeouts, and graceful failures built in.",
		icon: ShieldCheck,
	},
	{
		title: "Maintained integrations",
		description: "We add new integrations and keep existing ones working.",
		icon: RefreshCw,
	},
];

const PRODUCT_FEATURES: FeatureItem[] = [
	{
		title: "End-user isolation",
		description: "Scope teammates, memory, and runs per user with user_id.",
		icon: Users,
	},
	{
		title: "Permission controls",
		description: "Autonomous, approval, or plan mode. Pre-approve tools per user.",
		icon: Shield,
	},
	{
		title: "Per-user memory",
		description: "Persistent preferences and context across runs.",
		icon: Brain,
	},
	{
		title: "Webhook events",
		description: "run.completed, run.failed — push notifications to your backend.",
		icon: Webhook,
	},
	{
		title: "Streaming to your UI",
		description: "Forward SSE events directly to your frontend.",
		icon: Radio,
	},
	{
		title: "File output",
		description: "Teammates generate reports, exports, CSVs. Download via API.",
		icon: FileOutput,
	},
];

const OPS_FEATURES: FeatureItem[] = [
	{
		title: "Scheduling",
		description: "Cron triggers for weekly reports, daily syncs, hourly checks.",
		icon: Clock,
	},
	{
		title: "Email inbox",
		description: "Each teammate gets a unique email. Forward a thread, get work done.",
		icon: Mail,
	},
	{
		title: "Webhook triggers",
		description: "POST to a URL, trigger a run. Connect to any system.",
		icon: Webhook,
	},
	{
		title: "Follow-ups",
		description: "Continue conversations. Context carries over.",
		icon: MessageSquare,
	},
	{
		title: "Agent memory",
		description: "Teammates remember preferences and past runs.",
		icon: Brain,
	},
	{
		title: "70+ integrations",
		description: "Gmail, Slack, Notion, HubSpot, Stripe, Linear, and more.",
		icon: Blocks,
	},
];

const USE_CASES = [
	{
		title: "Multi-tenant SaaS",
		description:
			"Embed AI features in your product. Isolated memory, tools, and permissions per customer via user_id.",
	},
	{
		title: "Customer Support",
		description:
			"Triage tickets, draft replies, escalate to Slack. Trigger via email inbox or webhook — runs 24/7.",
	},
	{
		title: "Marketing & Ads",
		description:
			"Pause bad ad campaigns, add negative keywords, generate content briefs. Runs on a weekly schedule.",
	},
	{
		title: "Data Pipelines",
		description:
			"Pull Stripe MRR, sync CRM data, post summaries to Slack. Replace fragile Zapier chains with one teammate.",
	},
	{
		title: "Internal Ops",
		description:
			"Generate weekly reports, audit permissions, onboard new hires. Forward an email and get a researched reply back.",
	},
	{
		title: "Sales & Outreach",
		description:
			"Research prospects in HubSpot, draft personalized emails, log activity. Trigger by forwarding an email.",
	},
];
